import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Loader2, Wand2 } from "lucide-react";
import SiteShell from "../components/SiteShell";
import HoverChat from "../components/HoverChat";
import Action from "../components/Action";
import { listCommunityProjects, submitReservation, trackAction } from "../lib/api";
import { Field, inputStyle, primaryButtonStyle } from "./audit/StepEnergy";

/**
 * `/reserve` — Reserve a Sovereign Block.
 * Framing: co-ownership of a ≥250 kWh community asset. NEVER a home battery.
 * Payment is STUBBED in Phase 5 (see the "simulated payment" dev-note badge).
 */
const RESERVATION_FEE_INR = 5000;

// Indicative-only economics per block-size unit. Same shape as /audit's math
// but rounded aggressively — this is a citizen tool, not an engineering RFQ.
function estimateReturns(blockKwh) {
  const annualKwh = blockKwh * 300 * 0.9; // ~300 working days, 0.9 RTE
  const billCreditPerMonth = Math.round((annualKwh * 9) / 12); // ₹9/kWh avg
  const carbonRevenuePerYear = Math.round(annualKwh * 0.85 * 900 / 1000); // ₹900/tCO2
  const paybackYears = Math.round((blockKwh * 20000) / (billCreditPerMonth * 12 + carbonRevenuePerYear) * 10) / 10;
  return { annualKwh, billCreditPerMonth, carbonRevenuePerYear, paybackYears };
}

const BLOCK_SIZES = [1, 5, 10, 25];

export default function Reserve() {
  const [projects, setProjects] = useState([]);
  const [projectSlug, setProjectSlug] = useState("");
  const [blockKwh, setBlockKwh] = useState(5);
  const [contact, setContact] = useState({ name: "", email: "", phone: "", pan_or_passport: "" });
  const [waitlistOnly, setWaitlistOnly] = useState(false);
  const [paying, setPaying] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState(null);
  const [searchParams] = useSearchParams();
  const devBadge = process.env.NODE_ENV !== "production" || searchParams.get("dev") === "1";

  useEffect(() => {
    trackAction({ route: "reserve-block", source: "reserve", sourceState: "loaded" });
    (async () => {
      try {
        const list = await listCommunityProjects();
        setProjects(list);
        // Auto-select the first "active" project so the returns preview populates.
        const active = list.find((p) => p.status === "active");
        if (active) setProjectSlug(active.slug);
      } catch (ex) {
        setErr("Couldn't load community projects.");
      }
    })();
  }, []);

  const currentProject = useMemo(() => projects.find((p) => p.slug === projectSlug), [projects, projectSlug]);
  const returns = useMemo(() => estimateReturns(blockKwh), [blockKwh]);
  const emailOk = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(contact.email);
  const isWaitlist = waitlistOnly || currentProject?.status === "waitlist" || !projectSlug;
  const canSubmit = contact.name.trim() && emailOk && contact.phone.trim() && !busy && !paying;

  const simulatePayment = () => new Promise((res) => setTimeout(res, 1200));

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setErr("");
    try {
      let simulatedPaymentId = null;
      if (!isWaitlist) {
        setPaying(true);
        await simulatePayment();
        simulatedPaymentId = `SIM-${Math.random().toString(36).slice(2, 10).toUpperCase()}`;
        setPaying(false);
      }
      setBusy(true);
      const data = await submitReservation({
        reservation_type: isWaitlist ? "waitlist" : "block-reserve",
        project_slug: projectSlug || null,
        block_size_kwh: isWaitlist ? null : blockKwh,
        name: contact.name.trim(), email: contact.email.trim(), phone: contact.phone.trim(),
        pan_or_passport: contact.pan_or_passport.trim() || null,
        consent_to_terms: true,
        simulated_payment_id: simulatedPaymentId,
      });
      setOk(data);
      trackAction({ route: "reserve-block", source: "reserve", sourceState: "submitted", audience: data.status });
    } catch (ex) {
      setErr(ex?.response?.data?.detail || ex?.message || "Something went wrong.");
    } finally { setBusy(false); setPaying(false); }
  };

  return (
    <SiteShell>
      <section style={{ maxWidth: 1040, margin: "0 auto",
        padding: "clamp(52px, 8vw, 100px) clamp(20px, 5vw, 60px) clamp(50px, 7vw, 90px)" }}
        data-testid="reserve-page">
        <div className="gg-eyebrow" style={{ marginBottom: 10 }}>Reserve a Sovereign Block</div>
        <h1 className="gg-hero-title" style={{ marginBottom: 18, fontSize: "clamp(32px, 5vw, 56px)", maxWidth: "20ch" }} data-testid="reserve-headline">
          A titled share of a community asset. Your bill, running in reverse.
        </h1>
        <p style={{ color: "var(--text-dim)", fontSize: 16.5, lineHeight: 1.7, maxWidth: 720 }} data-testid="reserve-lede">
          A Sovereign Block is a{" "}
          <HoverChat term="fractional-ownership">titled fractional share</HoverChat>{" "}
          of a larger community energy asset (≥ 250 kWh — the size we actually
          build) — organised on the <HoverChat term="aavin">AAVIN / Anand pattern</HoverChat>.
          Not a rooftop unit. A cooperative. Your returns come as bill credits,
          monetised{" "}
          <HoverChat term="carbon-credits">carbon credits</HoverChat> and a{" "}
          <HoverChat term="sovereignty-score">Sovereignty Score</HoverChat>.
        </p>

        {/* How-it-works trio */}
        <div data-testid="reserve-how-it-works" style={{ display: "grid", gap: 14, marginTop: 32,
          gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))" }}>
          {[
            ["01", "A community project is scoped near you", "We size the ≥250 kWh asset around real households and businesses."],
            ["02", "You reserve a Sovereign Block", "A titled fractional share, priced fairly, transparent from day one."],
            ["03", "The block earns for you", "Bill credits + a carbon-credit share + a Sovereignty Score you can bank against."],
          ].map(([n, t, b]) => (
            <div key={n} className="gg-glass" style={{ padding: 16 }}>
              <div style={{ fontFamily: "Fraunces, Georgia, serif", fontSize: 32, color: "var(--gold-soft)", lineHeight: 1 }}>{n}</div>
              <div style={{ fontFamily: "Fraunces, Georgia, serif", fontSize: 16, color: "var(--paper)", marginTop: 8 }}>{t}</div>
              <div style={{ color: "var(--text-dim)", fontSize: 13, marginTop: 6, lineHeight: 1.55 }}>{b}</div>
            </div>
          ))}
        </div>

        {ok ? (
          <ReserveConfirmation data={ok} project={projects.find((p) => p.slug === ok.project_slug)} />
        ) : (
          <div style={{ marginTop: 36, padding: "22px 24px 26px", borderRadius: "var(--radius-lg)",
            background: "linear-gradient(180deg, rgba(255,255,255,0.05), rgba(255,255,255,0.01))",
            border: "1px solid var(--line-strong)", position: "relative" }} data-testid="reserve-flow">
            {devBadge && (
              <div data-testid="reserve-dev-badge" style={{
                position: "absolute", top: 10, right: 12, padding: "4px 8px", borderRadius: 6,
                background: "rgba(255,120,120,0.15)", border: "1px solid rgba(255,120,120,0.4)",
                color: "#ff8a8a", fontSize: 10.5, letterSpacing: "0.14em", textTransform: "uppercase",
              }}>DEV · payment simulated</div>
            )}

            <form onSubmit={submit}>
              {/* Step 1 — Location */}
              <div className="gg-eyebrow" style={{ marginBottom: 8 }}>Step 1 · Where</div>
              <Field label="Project location">
                <select value={projectSlug} onChange={(e) => setProjectSlug(e.target.value)} data-testid="reserve-project-select" style={inputStyle}>
                  <option value="" style={{ background: "var(--night-2)" }}>Notify me when a project opens near me</option>
                  {projects.map((p) => (
                    <option key={p.slug} value={p.slug} style={{ background: "var(--night-2)" }}>
                      {p.name} · {p.city}, {p.state} · {p.status}
                    </option>
                  ))}
                </select>
              </Field>
              {currentProject && (
                <div data-testid="reserve-project-info" style={{ marginTop: 8, color: "var(--text-dim)", fontSize: 13, lineHeight: 1.6 }}>
                  {currentProject.capacity_kwh_planned} kWh planned · next: {currentProject.next_milestone}
                </div>
              )}
              {(!projectSlug || currentProject?.status === "waitlist") && (
                <label style={{ marginTop: 10, display: "flex", gap: 8, alignItems: "center", cursor: "pointer" }}>
                  <input type="checkbox" checked={waitlistOnly} onChange={(e) => setWaitlistOnly(e.target.checked)} data-testid="reserve-waitlist-checkbox" style={{ accentColor: "var(--gold)" }} />
                  <span style={{ color: "var(--text-dim)", fontSize: 13.5 }}>Put me on the waitlist (no payment now)</span>
                </label>
              )}

              {/* Step 2 — Block size */}
              {!isWaitlist && (
                <>
                  <div className="gg-eyebrow" style={{ marginTop: 20, marginBottom: 8 }}>Step 2 · How much</div>
                  <div data-testid="reserve-block-sizes" style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    {BLOCK_SIZES.map((s) => {
                      const active = s === blockKwh;
                      return <button key={s} type="button" onClick={() => setBlockKwh(s)} data-testid={`reserve-block-${s}`}
                        style={{ padding: "10px 16px", borderRadius: 999,
                          background: active ? "linear-gradient(135deg, var(--gold), var(--gold-deep))" : "rgba(255,255,255,0.04)",
                          color: active ? "var(--night)" : "var(--text-cloud)", fontWeight: 600, fontSize: 13,
                          border: `1px solid ${active ? "transparent" : "var(--line)"}`, cursor: "pointer" }}>
                        {s} kWh
                      </button>;
                    })}
                  </div>
                  <div data-testid="reserve-returns" style={{ marginTop: 14, padding: 14, borderRadius: 12,
                    background: "rgba(244,169,31,0.06)", border: "1px solid rgba(244,169,31,0.25)",
                    color: "var(--text-cloud)", fontSize: 14, lineHeight: 1.75 }}>
                    Indicative for a <strong>{blockKwh} kWh</strong> block · bill credit ≈{" "}
                    <strong>₹{returns.billCreditPerMonth.toLocaleString("en-IN")}/mo</strong> · carbon revenue ≈{" "}
                    <strong>₹{returns.carbonRevenuePerYear.toLocaleString("en-IN")}/yr</strong> · indicative payback ≈{" "}
                    <strong>{returns.paybackYears} yrs</strong>.
                    <br />
                    <span style={{ color: "var(--text-mute)", fontSize: 12 }}>Indicative — not a guarantee. Refined at project scoping.</span>
                  </div>
                </>
              )}

              {/* Step 3 — Contact + payment */}
              <div className="gg-eyebrow" style={{ marginTop: 20, marginBottom: 8 }}>
                Step 3 · {isWaitlist ? "Get in touch" : "Reserve your block"}
              </div>
              <div style={{ display: "grid", gap: 14, gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))" }}>
                <Field label="Full name*"><input required value={contact.name} onChange={(e) => setContact({ ...contact, name: e.target.value })} data-testid="reserve-input-name" style={inputStyle} /></Field>
                <Field label="Email*"><input type="email" required value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} data-testid="reserve-input-email" style={{ ...inputStyle, borderColor: contact.email && !emailOk ? "rgba(255,120,120,0.5)" : "var(--line)" }} /></Field>
                <Field label="Phone*"><input required value={contact.phone} onChange={(e) => setContact({ ...contact, phone: e.target.value })} data-testid="reserve-input-phone" style={inputStyle} /></Field>
                <Field label="PAN / passport (optional)"><input value={contact.pan_or_passport} onChange={(e) => setContact({ ...contact, pan_or_passport: e.target.value })} data-testid="reserve-input-pan" style={inputStyle} /></Field>
              </div>

              {err && <p data-testid="reserve-error" style={{ marginTop: 12, color: "#ff8a8a", fontSize: 13 }}>{err}</p>}

              <div style={{ marginTop: 22, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                {!isWaitlist && (
                  <div style={{ color: "var(--text-dim)", fontSize: 13.5 }}>
                    Reservation fee <strong style={{ color: "var(--gold-soft)" }}>₹{RESERVATION_FEE_INR.toLocaleString("en-IN")}</strong>{" "}
                    <Action route="reserve-block" source="reserve" onClick={() => window.open("/policies/reservation", "_blank")}>(refundable per policy)</Action>
                  </div>
                )}
                <button type="submit" disabled={!canSubmit} data-testid="reserve-submit" style={primaryButtonStyle(!canSubmit)}>
                  {paying ? <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}><Wand2 size={14} /> Payment simulated…</span>
                   : busy ? <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}><Loader2 size={14} className="animate-spin" /> Reserving…</span>
                   : isWaitlist ? "Add me to the waitlist" : `Pay ₹${RESERVATION_FEE_INR.toLocaleString("en-IN")} to reserve`}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Reassurance tiles */}
        <div data-testid="reserve-reassurance" style={{ display: "grid", gap: 14, marginTop: 40,
          gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))" }}>
          {[
            ["Reservation ≠ final purchase", "Reserving locks priority; the full ownership deed follows at project close."],
            ["Clear terms, published", "The cooperative bylaws and Sovereign Block deed are published before you commit funds."],
            ["Refundable per policy", "If the project doesn't reach anchor threshold, your reservation fee is returned."],
          ].map(([t, b]) => (
            <div key={t} className="gg-glass" style={{ padding: 16 }}>
              <div style={{ fontFamily: "Fraunces, Georgia, serif", fontSize: 16, color: "var(--paper)" }}>{t}</div>
              <div style={{ color: "var(--text-dim)", fontSize: 13, marginTop: 6, lineHeight: 1.55 }}>{b}</div>
            </div>
          ))}
        </div>
      </section>
    </SiteShell>
  );
}

function ReserveConfirmation({ data, project }) {
  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} data-testid="reserve-confirmation"
      style={{ marginTop: 36, padding: 26, borderRadius: "var(--radius-lg)",
        background: "linear-gradient(180deg, rgba(31,170,89,0.14), rgba(31,170,89,0.02))",
        border: "1px solid rgba(31,170,89,0.4)" }}>
      <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 8 }}>
        <Check size={18} color="var(--green-bright)" />
        <span className="gg-eyebrow" style={{ color: "var(--green-bright)", margin: 0 }}>
          {data.status === "waitlist" ? "On the waitlist" : "Block reserved"}
        </span>
      </div>
      <h3 className="gg-h2">{data.message}</h3>
      <p style={{ marginTop: 12, fontFamily: "Fraunces, Georgia, serif", fontSize: 20, color: "var(--gold-soft)" }}>
        Reference: <span data-testid="reserve-ref-id">{data.ref_id}</span>
      </p>
      {project && data.status !== "waitlist" && (
        <p data-testid="reserve-project-name" style={{ marginTop: 6, color: "var(--text-dim)", fontSize: 14 }}>
          Project: {project.name} — {project.city}, {project.state} · block {data.block_size_kwh} kWh
        </p>
      )}
      <p style={{ marginTop: 16, color: "var(--text-cloud)", fontSize: 15.5, lineHeight: 1.7 }}>
        Want to see how the plant runs on its own sun? <Action route="book-tour" source="reserve-confirmation">Schedule a tour</Action>.
      </p>
    </motion.div>
  );
}
